/**
 * Structure Settings — ROROS-scoped tabbed page.
 *
 * TB-1 (AC-DM-01, AC-DM-07-view, AC-SSV-05):
 *   - Added "Decision Making" tab (last, after Constitution) — AC-DM-01
 *   - Removed page-level redirect for non-manage users — now all ROROS users
 *     can open Structure Settings; tabs filter by permission — AC-DM-07-view / AC-SSV-05
 *   - Deep-link ?tab=decision-making selects the Decision Making tab — AC-DM-09
 *   - Legacy ?tab=virtual-ceo still redirects → /roros/actors
 *   - Legacy ?edit-constitution=1 still deep-links to Constitution tab
 *
 * TB-4 (AC-SSV-01, AC-SSV-02, AC-SSV-04):
 *   - ALL 5 tabs are now visible to every org member (permission: null).
 *   - Editing is self-gated INSIDE each panel via hasPermission(<manage perm>).
 *   - Operating Mode / Domains / Cadence panels each render a read-only state
 *     when the user lacks the relevant manage permission (values shown, controls
 *     hidden, read-only hint displayed). Constitution + Decision Making already
 *     had read-only modes.
 *   - Members now see all 5 tabs → visibleTabs[0] = Operating Mode for everyone.
 *
 * ops-menu-reorg (AC-OPSNAV-2):
 *   - Added "Intake Sources" tab (last, after Decision Making), permission: null.
 *   - Deep-link ?tab=intake-sources selects the Intake Sources tab.
 *   - The ?edit-intake=<id> deep-link still opens the edit sheet from within the tab.
 *   - Writing stays gated server-side by ROROS_ARTIFACTS_EDIT (unchanged).
 *
 * FW2: activeTab is now DERIVED — no run-once correction effect needed.
 *   activeTab = userSelectedTab ?? deepLinkTab ?? visibleTabs[0]
 *   This eliminates the permissions-loading race. With all tabs visible to all
 *   users, visibleTabs[0] = Operating Mode immediately (no permissions-load wait).
 */

import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { RorosPage } from '@/components/roros/RorosPage';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { usePermissions } from '@/hooks/usePermissions';
import { Network } from 'lucide-react';
import { DomainsPanel } from '@/routes/roros/domains';
import { OperatingModePanel } from './panels/OperatingModePanel';
import { CadencePanel } from './panels/CadencePanel';
// NAV-1: Constitution moved here from System Settings (governance belongs in structure)
import { ConstitutionPanel } from './panels/ConstitutionPanel';
// TB-1: Decision Making tab — relocated from /roros/governance (AC-DM-01)
import { DecisionMakingPanel } from './panels/DecisionMakingPanel';
// AC-OPSNAV-2: Intake Sources panel — full management surface as the last tab.
import { IntakeSourcesPanel } from '@/routes/roros/intake/IntakeSourcesPanel';
import { useAuthStore } from '@/stores/authStore';
import { useOrgStore } from '@/stores/orgStore';

interface TabDef {
  value: string;
  label: string;
  permission: string | null; // null = always visible (all authenticated ROROS users)
  render: () => React.ReactNode;
}

// Valid tab values for the ?tab= deep-link (excluding virtual-ceo which redirects away).
const VALID_TAB_VALUES = [
  'operating-mode',
  'domains',
  'cadence',
  'constitution',
  'decision-making',
  'intake-sources',
] as const;

export function StructureSettingsPage() {
  const { hasPermission, isLoading: permissionsLoading } = usePermissions();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const { activeOrgId } = useOrgStore();
  const [searchParams] = useSearchParams();
  const editConstitutionParam = searchParams.get('edit-constitution');
  const tabParam = searchParams.get('tab');

  // TB-1 (AC-DM-07-view / AC-SSV-05): page-level redirect REMOVED.
  // All authenticated ROROS users can open Structure Settings.
  // Individual tabs self-gate on their manage permissions.
  // The redirect for non-ROROS users (no org) still applies via RorosGuard in AppRouter.

  // AC-NAV-4: ?tab=virtual-ceo graceful redirect → /roros/actors
  // Virtual CEO config is now accessible via the unified actor editor.
  React.useEffect(() => {
    if (tabParam === 'virtual-ceo') {
      navigate('/roros/actors', { replace: true });
    }
  }, [tabParam, navigate]);

  // TB-4 (AC-SSV-01): ALL tabs are now visible to every org member (permission: null).
  // Editing is self-gated INSIDE each panel via hasPermission(<manage perm>).
  // AC-OPSNAV-2: Intake Sources is the last tab; permission: null (visible to all).
  const allTabs: TabDef[] = [
    {
      // TB-4: permission null — all members see this tab (read-only for non-CEO).
      value: 'operating-mode',
      label: 'Operating Mode',
      permission: null,
      render: () => <OperatingModePanel />,
    },
    {
      // TB-4: permission null — all members see this tab (read-only for non-managers).
      value: 'domains',
      label: 'Domains',
      permission: null,
      render: () => <DomainsPanel />,
    },
    {
      // TB-4: permission null — all members see this tab (read-only for non-managers).
      value: 'cadence',
      label: 'Cadence',
      permission: null,
      render: () => <CadencePanel />,
    },
    {
      // NAV-1: Constitution tab moved from System Settings.
      // TB-4: permission null — all members see this tab (ConstitutionPanel self-gates editing).
      value: 'constitution',
      label: 'Constitution',
      permission: null,
      render: () => <ConstitutionPanel />,
    },
    {
      // TB-1 (AC-DM-01): Decision Making tab — always visible.
      value: 'decision-making',
      label: 'Decision Making',
      permission: null,
      render: () => <DecisionMakingPanel />,
    },
    {
      // AC-OPSNAV-2: Intake Sources tab — last, visible to all ROROS users.
      // Editing is gated server-side by ROROS_ARTIFACTS_EDIT (intake.routes.ts).
      // data-testid applied inline: structure-tab-intake-sources.
      value: 'intake-sources',
      label: 'Intake Sources',
      permission: null,
      render: () => <IntakeSourcesPanel />,
    },
  ];

  const visibleTabs = useMemo(
    () => allTabs.filter((tab) => tab.permission === null || hasPermission(tab.permission)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasPermission],
  );

  // FW2: activeTab is DERIVED — eliminates the permissions-loading timing race.
  //
  // Priority (highest → lowest):
  //   1. userSelectedTab — explicit tab click by the user
  //   2. deepLinkTab     — ?edit-constitution=1 or ?tab=<value> URL params (set once)
  //   3. visibleTabs[0]  — reactive default; tracks the first permitted tab as permissions resolve
  //
  // Before permissions finish loading, visibleTabs contains only null-permission tabs.
  // No run-once correction effect or ref needed.
  const [userSelectedTab, setUserSelectedTab] = useState<string | null>(null);
  const [deepLinkTab, setDeepLinkTab] = useState<string | null>(null);
  const activeTab = userSelectedTab ?? deepLinkTab ?? (visibleTabs[0]?.value ?? 'operating-mode');

  // FW1-F-02: each deep-link param gets its OWN consumed-once ref so a URL
  // carrying both params (?edit-constitution=1&tab=decision-making) cannot let
  // one silently block the other.  Previously a single shared ref caused the
  // second param's effect to bail out as soon as the first one fired.
  const editConstitutionConsumedRef = useRef(false);
  const tabParamConsumedRef = useRef(false);

  // Deep-link: ?edit-constitution=1 → constitution tab (existing behaviour)
  React.useEffect(() => {
    if (editConstitutionConsumedRef.current) return;
    if (permissionsLoading) return;
    if (!user || !activeOrgId) return;
    if (!editConstitutionParam) return;

    if (visibleTabs.some((t) => t.value === 'constitution')) {
      editConstitutionConsumedRef.current = true;
      setDeepLinkTab('constitution');
    }
  }, [permissionsLoading, user, activeOrgId, editConstitutionParam, visibleTabs]);

  // TB-1 (AC-DM-09) + AC-OPSNAV-2: Deep-link ?tab=<value> → select the named tab.
  // Handles all VALID_TAB_VALUES (decision-making, intake-sources, operating-mode, etc.).
  React.useEffect(() => {
    if (tabParamConsumedRef.current) return;
    if (permissionsLoading) return;
    if (!user || !activeOrgId) return;
    if (!tabParam) return;
    // Only act on known valid tab values (virtual-ceo is handled by its own redirect above).
    if (!(VALID_TAB_VALUES as readonly string[]).includes(tabParam)) return;

    if (visibleTabs.some((t) => t.value === tabParam)) {
      tabParamConsumedRef.current = true;
      setDeepLinkTab(tabParam);
    }
  }, [permissionsLoading, user, activeOrgId, tabParam, visibleTabs]);

  return (
    <RorosPage
      title="Structure Settings"
      icon={<Network className="h-5 w-5" />}
    >
      {visibleTabs.length === 0 ? (
        <div className="flex flex-col items-center justify-center min-h-[40vh] text-center p-8">
          <h2 className="text-lg font-semibold text-foreground mb-2">Access restricted</h2>
          <p className="text-sm text-muted-foreground max-w-md">
            You do not have permission to view any structure settings. Ask an
            administrator to grant ROROS permissions.
          </p>
        </div>
      ) : (
        <Tabs value={activeTab} onValueChange={setUserSelectedTab} className="mt-4">
          <TabsList>
            {visibleTabs.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value} data-testid={`structure-tab-${tab.value}`}>
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {visibleTabs.map((tab) => (
            <TabsContent key={tab.value} value={tab.value}>
              {tab.render()}
            </TabsContent>
          ))}
        </Tabs>
      )}
    </RorosPage>
  );
}
